import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import React, { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ActiveCatalogProvider } from './contexts/ActiveCatalogContext';
import { CollectionsProvider } from './contexts/CollectionsContext';
import { CatalogProvider } from './contexts/CatalogContext';
import { NavigationBar } from './components/NavigationBar';
import { Footer } from './components/Footer';
import { NewVersionBanner } from './components/NewVersionBanner';
import { IncidentBanner } from './components/IncidentBanner';
import { AccountExpiredBanner } from './components/AccountExpiredBanner';
import { useIncidentBanner } from './hooks/useIncidentBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleProtectedRoute } from './components/RoleProtectedRoute';
import { DefaultRedirect } from './components/DefaultRedirect';
import { useSessionPersistence } from './hooks/useSessionPersistence';
import { useVersionCheck } from './hooks/useVersionCheck';
import { Home } from './pages/Home';
import { MyObjects } from './pages/MyObjects';
import { Signup } from './pages/Signup';
import { SignupSuccess } from './pages/SignupSuccess';
import { ChangePassword } from './pages/ChangePassword';
import { ForgotPassword } from './pages/ForgotPassword';
import { UploadObject } from './pages/UploadObject';
import { EditObject } from './pages/EditObject';
import { CurriculumVitae } from './pages/CurriculumVitae';
import { PublicCV } from './pages/PublicCV';
import { CVEditPage } from './pages/CVEditPage';
import { Settings } from './pages/Settings';
import { ObjectDetails } from './pages/ObjectDetails';
import { Bookmarks } from './pages/Bookmarks';
import { Activities } from './pages/Activities';
import { BookmarkProvider } from './contexts/BookmarkContext';
import { SearchProvider } from './contexts/SearchContext';
import { VisibilityProvider } from './contexts/VisibilityContext';
import { GettyFavoritesProvider } from './contexts/GettyFavoritesContext';
import { FieldFavoritesProvider } from './contexts/FieldFavoritesContext';
import { PublicCatalog } from './pages/PublicCatalog';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { PendingInvitationRedirect } from './components/PendingInvitationRedirect';
import { NotFound } from './pages/NotFound';
import { TestObjectPicker } from './pages/TestObjectPicker';
import { Collections } from './pages/Collections';
import { CollectionSettings } from './pages/CollectionSettings';
import SetupMFA from './pages/SetupMFA';
import Admin from './pages/Admin';
import BulkUpload from './pages/BulkUpload';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import SearchPage from './pages/SearchPage';
import { featureFlags } from './utils/featureFlags';

// Redirect /collections/:collectionId/settings → /albums/:collectionId/settings
const CollectionSettingsRedirect = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  return <Navigate to={`/albums/${collectionId}/settings`} replace />;
};

// Lazy load with chunk error recovery (max 2 retries per session)
function lazyWithReload<T extends React.ComponentType>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch(() => {
      const key = 'chunk-reload-count';
      const reloads = Number(sessionStorage.getItem(key) || '0');
      if (reloads < 2) {
        sessionStorage.setItem(key, String(reloads + 1));
        window.location.reload();
      }
      return { default: (() => null) as unknown as T };
    })
  );
}

// Lazy load payment-related pages to avoid loading Stripe SDK on every page
const Auth = lazyWithReload(() => import('./pages/Auth').then(m => ({ default: m.Auth })));
const PaymentSuccess = lazyWithReload(() => import('./pages/PaymentSuccess').then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazyWithReload(() => import('./pages/PaymentCancel').then(m => ({ default: m.PaymentCancel })));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

const AppContent = () => {
  useSessionPersistence();
  const { newVersionAvailable, dismiss, refresh } = useVersionCheck();
  const { bannerData, dismissed: incidentDismissed, dismiss: dismissIncident } = useIncidentBanner();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <IncidentBanner
        visible={bannerData.enabled && !incidentDismissed}
        message={bannerData.message}
        details={bannerData.details}
        severity={bannerData.severity}
        onDismiss={dismissIncident}
      />
      <NewVersionBanner
        visible={newVersionAvailable}
        onRefresh={refresh}
        onDismiss={dismiss}
      />
      <AccountExpiredBanner />
      <PendingInvitationRedirect />
      <Box sx={{ flex: 1 }}>
        <Routes>
          <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
          <Route path="/invitation/accept" element={<AcceptInvitation />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signup-success" element={<SignupSuccess />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/payment/success" element={<Suspense fallback={<PageLoader />}><PaymentSuccess /></Suspense>} />
          <Route path="/payment/cancel" element={<Suspense fallback={<PageLoader />}><PaymentCancel /></Suspense>} />
          <Route path="/setup-mfa" element={<SetupMFA />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <NavigationBar />
              <Routes>
                <Route index element={<DefaultRedirect />} />
                <Route path="home" element={<Home />} />
                <Route path="bookmarks" element={<Bookmarks />} />
                <Route path="settings" element={<Settings />} />
                {featureFlags.isEnabled('enableSearchPageRedesign') && (
                  <Route path="search" element={<SearchPage />} />
                )}
                <Route path="object-details/:artworkId" element={<ObjectDetails />} />

                {/* Protected routes for catalog owners and admins only */}
                <Route path="my-objects" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <MyObjects />
                  </RoleProtectedRoute>
                } />
                <Route path="albums" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <Collections />
                  </RoleProtectedRoute>
                } />
                <Route path="albums/:collectionId/settings" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <CollectionSettings />
                  </RoleProtectedRoute>
                } />
                {/* Redirect old /collections paths to /albums */}
                <Route path="collections" element={<Navigate to="/albums" replace />} />
                <Route path="collections/:collectionId/settings" element={<CollectionSettingsRedirect />} />
                <Route path="upload-object" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    {featureFlags.isEnabled('enableEditObjectRedesign') ? <EditObject /> : <UploadObject />}
                  </RoleProtectedRoute>
                } />
                <Route path="edit-object" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    {featureFlags.isEnabled('enableEditObjectRedesign') ? <EditObject /> : <UploadObject />}
                  </RoleProtectedRoute>
                } />
                <Route path="activities" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <Activities />
                  </RoleProtectedRoute>
                } />
                <Route path="curriculum-vitae" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <CurriculumVitae />
                  </RoleProtectedRoute>
                } />
                <Route path="cv" element={<Navigate to="/cv/edit" replace />} />
                <Route path="cv/edit" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <CVEditPage />
                  </RoleProtectedRoute>
                } />
                <Route path="cv/public/:aliId" element={<PublicCV />} />
                <Route path="public/catalog" element={<PublicCatalog />} />
                <Route path="public/catalog/:aliId" element={<PublicCatalog />} />
                <Route path="public/object-details/:artworkId" element={<ObjectDetails />} />
                <Route path="test-picker" element={<TestObjectPicker />} />
                <Route path="admin" element={
                  <RoleProtectedRoute allowedRoles={['admin']}>
                    <Admin />
                  </RoleProtectedRoute>
                } />
                <Route path="bulk-upload" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'catalog-owner', 'delegate']}>
                    <BulkUpload />
                  </RoleProtectedRoute>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          } />
        </Routes>
      </Box>
      <Footer />
    </Box>
  );
};

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <ActiveCatalogProvider>
        <CollectionsProvider>
          <CatalogProvider>
            <SearchProvider>
              <BookmarkProvider>
                <VisibilityProvider>
                  <GettyFavoritesProvider>
                    <FieldFavoritesProvider>
                      <Router>
                        <AppContent />
                      </Router>
                    </FieldFavoritesProvider>
                  </GettyFavoritesProvider>
                </VisibilityProvider>
              </BookmarkProvider>
            </SearchProvider>
          </CatalogProvider>
        </CollectionsProvider>
        </ActiveCatalogProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
